import { type Editor, getMarkRange, type JSONContent, type Range } from '@tiptap/react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

export const LINK_MARK_NAME = 'link';

const URL_SCHEME_REGEX = /^[a-z][a-z0-9+.-]*:/i;
const EMAIL_ADDRESS_REGEX = /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/;
const MAILTO_SCHEME = 'mailto:';
const DEFAULT_LINK_SCHEME = 'https://';
const IMAGE_SOURCE_REGEX = /^https?:\/\/\S+$/i;
const BLOCK_SEPARATOR = ' ';
const INLINE_LEAF_TEXT = ' ';
const TRAILING_URL_REGEX = /(?:https?:\/\/|www\.)\S+$/i;
const BARE_URL_REGEX = /^(?:https?:\/\/|www\.)\S+$/i;
const TRAILING_PUNCTUATION_REGEX = /[.,;:!?)\]}'"]+$/;
const PREVENT_AUTOLINK_META = 'preventAutolink';

export interface TemplateLinkDraft {
  url: string;
  text: string;
  originalText: string;
  from: number;
  to: number;
  editing: boolean;
}

export const toTemplateLinkHref = (url: string): string => {
  const trimmedUrl = url.trim();
  if (isEmpty(trimmedUrl) || URL_SCHEME_REGEX.test(trimmedUrl)) {
    return trimmedUrl;
  }
  if (EMAIL_ADDRESS_REGEX.test(trimmedUrl)) {
    return `${MAILTO_SCHEME}${trimmedUrl}`;
  }
  return `${DEFAULT_LINK_SCHEME}${trimmedUrl}`;
};

export const isTemplateImageSource = (source: string): boolean => IMAGE_SOURCE_REGEX.test(source.trim());

const findLinkRange = (editor: Editor): Range | null => {
  if (!editor.isActive(LINK_MARK_NAME)) {
    return null;
  }
  const { state } = editor;
  const range = getMarkRange(state.doc.resolve(state.selection.from), state.schema.marks[LINK_MARK_NAME]);
  return range ? range : null;
};

export const readTemplateLinkDraft = (editor: Editor): TemplateLinkDraft => {
  const { state } = editor;
  const linkRange = findLinkRange(editor);
  if (!isNil(linkRange)) {
    const text = state.doc.textBetween(linkRange.from, linkRange.to, BLOCK_SEPARATOR);
    const href = editor.getAttributes(LINK_MARK_NAME).href;
    return {
      url: isString(href) ? href : '',
      text,
      originalText: text,
      from: linkRange.from,
      to: linkRange.to,
      editing: true,
    };
  }
  const { from, to } = state.selection;
  const text = state.doc.textBetween(from, to, BLOCK_SEPARATOR);
  return { url: '', text, originalText: text, from, to, editing: false };
};

export const canApplyTemplateLink = (editor: Editor, draft: TemplateLinkDraft): boolean => {
  const href = toTemplateLinkHref(draft.url);
  return !isEmpty(href) && editor.can().setLink({ href });
};

export const applyTemplateLinkDraft = (editor: Editor, draft: TemplateLinkDraft): boolean => {
  if (!canApplyTemplateLink(editor, draft)) {
    return false;
  }
  const href = toTemplateLinkHref(draft.url);
  const text = isEmpty(draft.text.trim()) ? href : draft.text;
  const range = { from: draft.from, to: draft.to };
  if (text === draft.originalText && draft.from !== draft.to) {
    return editor
      .chain()
      .setTextSelection(range)
      .setLink({ href })
      .setTextSelection(draft.to)
      .unsetMark(LINK_MARK_NAME)
      .run();
  }
  const marks = editor.state.doc
    .resolve(draft.from)
    .marks()
    .reduce<NonNullable<JSONContent['marks']>>(
      (acc, mark) => (mark.type.name === LINK_MARK_NAME ? acc : [...acc, { type: mark.type.name, attrs: mark.attrs }]),
      [{ type: LINK_MARK_NAME, attrs: { href } }]
    );
  return editor.chain().insertContentAt(range, { type: 'text', text, marks }).unsetMark(LINK_MARK_NAME).run();
};

export const removeTemplateLink = (editor: Editor, draft: TemplateLinkDraft): boolean =>
  editor.chain().setTextSelection({ from: draft.from, to: draft.to }).unsetLink().setTextSelection(draft.to).run();

export const linkifyUrlBeforeCaret = (editor: Editor): boolean => {
  if (editor.isDestroyed || !editor.isEditable) {
    return false;
  }
  const { state } = editor;
  const linkType = state.schema.marks[LINK_MARK_NAME];
  const { selection } = state;
  if (isNil(linkType) || !selection.empty) {
    return false;
  }
  const { $from } = selection;
  const textBeforeCaret = $from.parent.textBetween(0, $from.parentOffset, undefined, INLINE_LEAF_TEXT);
  const trailingUrl = textBeforeCaret.match(TRAILING_URL_REGEX)?.[0];
  if (isNil(trailingUrl)) {
    return false;
  }
  const url = trailingUrl.replace(TRAILING_PUNCTUATION_REGEX, '');
  if (!BARE_URL_REGEX.test(url)) {
    return false;
  }
  const from = $from.pos - trailingUrl.length;
  const to = from + url.length;
  if (state.doc.rangeHasMark(from, to, linkType)) {
    return false;
  }
  const href = toTemplateLinkHref(url);
  editor.view.dispatch(state.tr.addMark(from, to, linkType.create({ href })).setMeta(PREVENT_AUTOLINK_META, true));
  return true;
};
